/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {InputTypes} from '../types/editors';

export interface UseSecureFieldParams {
  formValue: string;
  hideCharacters?: boolean;
  fallbackValue?: string;
}

export interface UseSecureFieldResult {
  value: string;
  inputType: string;
  toggleVisibility: () => void;
  onFocus: () => void;
  onBlur: () => void;
}

/**
 * @useSecureField 
 * 
 * Hook that encapsulates logic for showing / obscuring a field's text. 
 * 
 * This clears the field on focus when editing, and saves the entered secure field on blur.
 * 
 * ```typescript
 *  
 * import useSecureField from '@ion/dls/hooks/useSecureField';
 * 
 * const {
 *   value,
 *   inputType,
 *   flipVisibility,
 *   onFocus,
 *   onBlur
 * } = useSecureField({
 *   formValue,
 *   fallbackValue,
 *   hideCharacters
 * });
 * 
 * ```
 */
export const useSecureField = ({
  formValue,
  hideCharacters = true,
  fallbackValue = 'placeholder'
}: UseSecureFieldParams): UseSecureFieldResult => {
  const [inputType, setInputType] = useState(hideCharacters ? InputTypes.PASSWORD : InputTypes.TEXT);
  const [value, setValue]         = useState(fallbackValue);

  useEffect(function(): void {
    if (value !== fallbackValue || fallbackValue === '') {
      setValue(formValue);
    }
  }, [formValue]);

  const onFocus = (): void => {
    setValue(value !== fallbackValue ? value : '');
  };

  const onBlur = (): void => {
    setValue(value !== '' ? value : fallbackValue);
  };

  const toggleVisibility = (): void => {
    setInputType(inputType === InputTypes.PASSWORD ? InputTypes.TEXT : InputTypes.PASSWORD);
  };

  return {
    value,
    inputType,
    toggleVisibility,
    onFocus,
    onBlur
  };
};

export default useSecureField;
